/**
 * Items v2 stats wiring (docs/systems/ITEMS_V2.md §5.2, R2): the affixes that are not plain ratings, at the World's existing
 * hook points. Each helper returns the neutral value (1 or 0) for anyone but the player, and for a player with none of
 * that affix, so a world without such gear runs exactly as before.
 *
 * - **Hits** (world.ts `hit`): `gearHitMul` (art and school damage, แรงคริ on a crit, แรงทำลายการ์ด on a ท่าทำลาย that
 *   breaks a guard, ความเสียหายสวนกลับ on the first blow within `GEAR_COMBAT.counterWindow` s of a successful ตั้งรับ),
 *   `gearDefMul` (เจาะเกราะ), `gearCritRes` (ลดคริที่โดน), `gearStunMul` (แรงมึนงง), `gearGuardMul` (guard stamina
 *   taken: แรงทำลายการ์ด out, ความทนการ์ด in), `gearRageMul` (ความโกรธที่ได้).
 * - **Casting**: `gearQiMul` (ลดลมปราณที่ใช้), `gearCdMul` (ลดคูลดาวน์), `gearWindupMul` (ลดเวลาชาร์จท่าทำลาย; the wind-up
 *   stays, capped at −10 % by `AFFIX_CAPS`).
 * - **Qinggong**: `gearQinggong` (ลดพลังกายตอนเหาะ on every qinggong stamina cost, ระยะร่อน as a slower glide fall) and
 *   `gearFallMul` (ลดความเสียหายตก).
 * - **Statuses** (this system's `observe`): a landed blow of the player rolls โอกาสพิษ / โอกาสเลือดไหล (one draw each,
 *   only when the chance is above 0); พิษ / เลือดไหล on the player last ×(1 − ต้านพิษ / ต้านเลือดไหล).
 */
import { ITEMS, type EquipmentStats } from '../data/items';
import { ITEM_SETS, SET_FX, setBonusesAt } from '../data/itemSets';
import { MARTIAL_ARTS, SKILLS } from '../data/skills';
import { UNIQUES, type UniqueEffect } from '../data/uniques';
import { STATUSES } from '../status';
import { gearFor } from '../gearStats';
import type { QinggongStats } from '../qinggong';
import type { Entity, World, WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

export const GEAR_COMBAT = {
  /** Seconds after a successful ตั้งรับ in which the player's next blow is a counter. */
  counterWindow: 2,
} as const;

const SYSTEMS = new WeakMap<World, GearCombatSystem>();

/**
 * A player's gear sum (per entity: every player of a World has their own), or undefined for anyone else (or before
 * the first stats refresh). `w` stays in the signatures for the hooks that need the World.
 */
// In a fair-arena bout (tournament) gear counts for nothing.
const mine = (w: World, e: Entity) => (e.kind === 'player' && !w.pvpBout?.fair ? gearFor(e) : undefined);

/** Damage multiplier of a player's blow with `artId`. The first term is the old art bonus, unchanged. */
export function gearHitMul(
  w: World,
  src: Entity,
  artId: string,
  res: { readonly crit: boolean; readonly outcome: string },
): number {
  const g = mine(w, src);
  if (!g) return 1;
  let m = 1 + (g.arts[artId] ?? 0);
  const school = MARTIAL_ARTS[artId]?.school;
  if (school && g.schools[school]) m += g.schools[school]!;
  if (res.crit && g.critDmg) m += g.critDmg;
  if (res.outcome === 'guardBreak' && g.guardBreak) m += g.guardBreak;
  if (g.counterDmg && SYSTEMS.get(w)?.countering(src)) m += g.counterDmg;
  if (g.airDmg && src.z > 0) m += g.airDmg;
  return m;
}

/** The target's defence multiplier against the player's เจาะเกราะ. */
export const gearDefMul = (w: World, src: Entity): number => 1 - (mine(w, src)?.defPierce ?? 0);
/** Crit chance taken off blows on the player. */
export const gearCritRes = (w: World, tgt: Entity): number => mine(w, tgt)?.critRes ?? 0;
/** The player's stuns last longer. */
export const gearStunMul = (w: World, src: Entity): number => 1 + (mine(w, src)?.stunPower ?? 0);
/** Guard stamina a blow takes: more from the player's, less off the player's guard. */
export const gearGuardMul = (w: World, src: Entity, tgt: Entity): number =>
  (1 + (mine(w, src)?.guardBreak ?? 0)) * (1 - (mine(w, tgt)?.guardHp ?? 0));
/** Rage the player gains. */
export const gearRageMul = (w: World, e: Entity): number => 1 + (mine(w, e)?.rageGain ?? 0);
/** กำลังภายใน a move costs the player (a volley costs more with กระบี่ซ่อนเงา). */
export function gearQiMul(w: World, e: Entity, skill?: { readonly volley?: number }): number {
  const g = mine(w, e);
  if (!g) return 1;
  const m = 1 - g.qiCostPct;
  const more = skill?.volley ? unique(g, 'volleyExtra') : null;
  return more ? m * more.qiMul : m;
}

/** กระบี่ซ่อนเงา: the extra swords of a volley and their share of its power, or null. */
export function gearVolleyExtra(w: World, e: Entity): { extra: number; pct: number } | null {
  const g = mine(w, e);
  const fx = g && unique(g, 'volleyExtra');
  return fx ? { extra: fx.extra, pct: fx.pct } : null;
}
/** A move's cooldown for the player. */
export const gearCdMul = (w: World, e: Entity): number => 1 - (mine(w, e)?.cdPct ?? 0);
/** A move's wind-up for the player: only ท่าทำลาย shorten. */
export const gearWindupMul = (w: World, e: Entity, kind: string): number =>
  kind === 'break' ? 1 - (mine(w, e)?.breakWindup ?? 0) : 1;
/**
 * An air dash's stamina multiplier: 1 for the first; for a second, รองเท้าเมฆาเหิน's (and it is spent until the wearer
 * lands); 0 when none is left.
 */
export function gearAirDashMul(w: World, e: Entity): number {
  if (!e.airDashed) return 1;
  const g = mine(w, e);
  const fx = g && unique(g, 'airDash');
  const sys = SYSTEMS.get(w);
  if (!fx || !sys || sys.secondDash.has(e.id)) return 0;
  sys.secondDash.add(e.id);
  return fx.staminaMul;
}

/** The share of a fall's damage the player takes. */
export const gearFallMul = (w: World, e: Entity = w.player): number => 1 - (gearFor(e)?.fallRes ?? 0);

/** What the qinggong cache keys on besides the levels. */
export const gearQinggongKey = (w: World, e: Entity = w.player): string => {
  const g = gearFor(e);
  return g && (g.flyCostPct || g.glideLen) ? `|${g.flyCostPct}|${g.glideLen}` : '';
};

/** The player's qinggong numbers with the gear's: stamina costs ×(1 − ลดพลังกายตอนเหาะ), glide fall ÷(1 + ระยะร่อน). */
export function gearQinggong(w: World, s: QinggongStats, e: Entity = w.player): QinggongStats {
  const g = gearFor(e);
  const cost = 1 - (g?.flyCostPct ?? 0);
  const glide = 1 + (g?.glideLen ?? 0);
  if (cost === 1 && glide === 1) return s;
  const c = (v: number) => Math.round(v * cost * 10) / 10;
  return {
    ...s,
    dash: { ...s.dash, stamina: c(s.dash.stamina) },
    sprint: s.sprint && { ...s.sprint, stamina: c(s.sprint.stamina) },
    airJumpStamina: c(s.airJumpStamina),
    climb: s.climb && { ...s.climb, stamina: c(s.climb.stamina) },
    water: s.water && { ...s.water, stamina: c(s.water.stamina) },
    glide: s.glide && { ...s.glide, stamina: c(s.glide.stamina), fall: s.glide.fall / glide },
  };
}

const LANDED = new Set(['hit', 'interrupt', 'guardBreak']);

/** Whether the gear's sets pay `effect`. */
const setEffect = (g: EquipmentStats, effect: string): boolean =>
  Object.entries(g.sets).some(([id, n]) => {
    const def = ITEM_SETS[id];
    return !!def && setBonusesAt(def, n).some((b) => b.effect === effect);
  });

/** The live unique effect of `kind` the gear wears, if any. */
function unique<K extends UniqueEffect['kind']>(g: EquipmentStats, kind: K): Extract<UniqueEffect, { kind: K }> | null {
  for (const id of g.uniques) {
    const u = UNIQUES[id];
    if (u?.live && u.effect.kind === kind) return u.effect as Extract<UniqueEffect, { kind: K }>;
  }
  return null;
}

/** Foes (monsters, rivals) within `r` m of `at`, but `not`. */
function foesNear(w: World, at: Entity, r: number, not?: Entity): Entity[] {
  const out: Entity[] = [];
  for (const e of w.entities.values())
    if (e !== not && !e.dead && (e.kind === 'monster' || e.kind === 'rival') && Math.hypot(e.x - at.x, e.y - at.y) <= r)
      out.push(e);
  return out;
}

export class GearCombatSystem implements WorldSystem {
  readonly name = 'gearCombat';
  private now = 0;
  /** Player entity id → charges of ชุดกระดองอสนี. */
  private readonly charges = new Map<number, number>();
  /** Player entity id → when they last blocked a blow. */
  private readonly blockedAt = new Map<number, number>();

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  /** Players who have used their second air dash since they last landed. */
  readonly secondDash = new Set<number>();

  step(dt: number): void {
    this.now += dt;
    for (const id of this.secondDash) if (!this.h.world.get(id)?.airDashed) this.secondDash.delete(id);
  }

  /** Whether this player's next blow is a counter (and uses it up). */
  countering(e: Entity): boolean {
    const at = this.blockedAt.get(e.id);
    if (at === undefined || this.now - at > GEAR_COMBAT.counterWindow) return false;
    this.blockedAt.delete(e.id);
    return true;
  }

  /** Set and unique effects of a player's landed blow. */
  private onLanded(src: Entity, tgt: Entity, g: EquipmentStats, skillId: string, crit: boolean): void {
    const w = this.h.world;
    // ชุดขนนกเพลิง 4: a crit sets the foe burning.
    if (crit && setEffect(g, 'critBurn')) w.addStatus(tgt, 'poison', SET_FX.burnStacks, src.id);
    // กระบองทลายภูผา: a landed ท่าทำลาย stuns the foes round the target.
    const quake = unique(g, 'quakeRing');
    if (quake && SKILLS[skillId]?.kind === 'break')
      for (const e of foesNear(w, tgt, quake.reach, tgt)) e.stunT = Math.max(e.stunT, quake.stun);
    // ดาบโลหิตกลืนจันทร์: a foe bleeding enough has it all drained at once.
    const drain = unique(g, 'bleedConsume');
    const bleed = tgt.statuses.find((s) => s.id === 'bleed');
    if (drain && bleed && bleed.stacks >= drain.min && !tgt.dead) {
      const n = bleed.stacks;
      w.addStatus(tgt, 'bleed', -n);
      tgt.hp = Math.max(1, tgt.hp - Math.round(tgt.maxHp * drain.perStack * n));
    }
    // เข็มเงาเหมยแดง: the hidden weapon's throw poisons and slows; five poison stacks silence.
    const needles = unique(g, 'needlePoison');
    const hidden = src.kind === 'player' ? this.h.world.progress.equipment.hidden : undefined;
    const throwSkill = hidden ? ITEMS[this.h.world.progress.rolls[hidden]?.base ?? hidden]?.throwSkill : undefined;
    if (needles && throwSkill && skillId === throwSkill && !tgt.dead) {
      w.addStatus(tgt, 'poison', 1, src.id);
      w.addStatus(tgt, 'slow', 1, src.id);
      if ((tgt.statuses.find((s) => s.id === 'poison')?.stacks ?? 0) >= needles.silenceAt)
        w.addStatus(tgt, 'silence', 1, src.id);
    }
  }

  /** ชุดกระดองอสนี 4: blocks charge; a full charge stuns the foes round the wearer. */
  private blockCharge(p: Entity): void {
    const g = gearFor(p);
    if (!g || !setEffect(g, 'blockCharge')) return;
    const n = (this.charges.get(p.id) ?? 0) + 1;
    if (n < SET_FX.charges) return void this.charges.set(p.id, n);
    this.charges.set(p.id, 0);
    for (const e of foesNear(this.h.world, p, SET_FX.ringReach)) e.stunT = Math.max(e.stunT, SET_FX.ringStun);
  }

  observe(ev: WorldEvent): void {
    const w = this.h.world;
    if (ev.type === 'hit') {
      const blocker = ev.outcome === 'blocked' ? w.get(ev.tgtId) : undefined;
      if (blocker?.kind === 'player') {
        this.blockedAt.set(blocker.id, this.now);
        this.blockCharge(blocker);
      }
      const src = w.get(ev.srcId);
      const g = src && src.kind === 'player' ? gearFor(src) : undefined;
      if (!src || !g || !LANDED.has(ev.outcome) || ev.damage <= 0) return;
      const tgt = w.get(ev.tgtId);
      if (!tgt || tgt.dead) return;
      if (g.poisonChance > 0 && this.h.random() < g.poisonChance) w.addStatus(tgt, 'poison', 1, src.id);
      if (g.bleedChance > 0 && this.h.random() < g.bleedChance) w.addStatus(tgt, 'bleed', 1, src.id);
      this.onLanded(src, tgt, g, ev.skillId, ev.crit);
      return;
    }
    if (ev.type === 'skill') {
      const src = w.get(ev.srcId);
      const g = src?.kind === 'player' ? gearFor(src) : undefined;
      // พัดสายลมนิรนาม: a ท่าเสริม cleanses one debuff from the caster.
      if (src && g && SKILLS[ev.skillId]?.kind === 'buff' && unique(g, 'buffCleanse')) {
        const st = src.statuses.find((s) => STATUSES[s.id]?.kind === 'debuff' && s.id !== 'stun');
        if (st) w.addStatus(src, st.id, -st.stacks);
      }
      return;
    }
    if (ev.type === 'status' && ev.stacks > 0) {
      const e = w.get(ev.id);
      const g = e?.kind === 'player' ? gearFor(e) : undefined;
      const res = ev.status === 'poison' ? g?.poisonRes : ev.status === 'bleed' ? g?.bleedRes : 0;
      if (!res) return;
      const st = e!.statuses.find((s) => s.id === ev.status);
      if (st) st.t *= 1 - res;
    }
  }
}
